import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SessionProgressIndicator from '../../components/ui/SessionProgressIndicator';
import CollaborativeStatusBar from '../../components/ui/CollaborativeStatusBar';
import ShareSessionControl from '../../components/ui/ShareSessionControl';
import ItemCard from './components/ItemCard';
import UserSummaryPanel from './components/UserSummaryPanel';
import ConflictResolutionModal from './components/ConflictResolutionModal';
import ParticipantsList from './components/ParticipantsList';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { subscribeToSession, getSessionFromCloud, saveSessionToCloud, updateUserClaims, saveUserClaim, getUserClaims, getAllSessionClaims, subscribeToSessionClaims } from '../../utils/supabaseService';
import { getCurrentUser, getActiveSessionId } from '../../utils/sessionStorage';

const ItemSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictItem, setConflictItem] = useState(null);
  const [conflictingUsers, setConflictingUsers] = useState([]);
  const [sidebarView, setSidebarView] = useState('summary'); // 'summary' | 'participants'
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [realtimeBillData, setRealtimeBillData] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(true);

  // Get data from previous page (bill-overview)
  const locationState = location.state || {};

  // 🔄 Recuperar usuario de localStorage si no viene en el state
  const storedUser = getCurrentUser();
  const storedSessionId = getActiveSessionId();

  // Usar datos del state, o recuperar de localStorage como fallback
  const sessionId = locationState.sessionId || storedSessionId;
  const initialBillData = locationState.billData;
  const userName = locationState.userName || storedUser?.userName;
  const userId = locationState.userId || storedUser?.userId;
  const isGuest = locationState.isGuest || false;
  const isHost = locationState.isHost ?? storedUser?.isHost ?? false;

  // Check if there's an active session
  useEffect(() => {
    if (!sessionId && !initialBillData) {
      setHasActiveSession(false);
    }
  }, [sessionId, initialBillData]);

  // Use realtime data if available, otherwise use initial data
  const billData = realtimeBillData || initialBillData;

  // Use real bill data if available, otherwise fallback to empty array
  const [participants] = useState(billData?.participants || []
  );

  // Get current user ID - usa userId del state/localStorage si está disponible
  // Fallback: busca en participants por userName o isHost
  const currentUserId = userId
    || (isHost ? participants.find(p => p.isHost)?.id : participants.find(p => p.name === userName)?.id)
    || userName;
  console.log('👤 Current user:', userName, 'ID:', currentUserId, 'isHost:', isHost);
  console.log('👥 All participants:', participants);
  console.log('📦 StoredUser from localStorage:', storedUser);

  // Transform bill items to the format expected by item-selection
  // For guests: only show items that have been validated by host
  const transformedBillItems = billData?.items
    ?.filter(item => {
      // If guest, only show validated items
      if (isGuest) {
        return item.validated === true;
      }
      // Host sees all items
      return true;
    })
    ?.map(item => {
      // Calculate unit price correctly
      const quantity = item.quantity || 1;
      const unitPrice = item.unitPrice || (item.totalPrice / quantity) || item.price || 0;

      return {
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: unitPrice,
        unitPrice: unitPrice,
        totalPrice: item.totalPrice || (unitPrice * quantity),
        quantity: quantity,
        category: item.category || 'otros',
        allergens: item.allergens || [],
        validated: item.validated || false,
        claims: item.claims || [], // Preserve existing claims
        shared: item.shared || false,
        sharedWith: item.sharedWith || []
      };
    }) || [];

  const [billItems, setBillItems] = useState(transformedBillItems);

  // Cargar claims desde user_claims al montar
  useEffect(() => {
    if (!sessionId) return;

    console.log('📥 Loading claims from user_claims table...');
    getAllSessionClaims(sessionId).then(result => {
      if (result.success && result.data) {
        console.log(`✅ Loaded ${result.data.length} claims from database`);

        // Convertir claims de la tabla a formato de billItems
        setBillItems(prevItems => {
          return prevItems.map(item => {
            // Encontrar todos los claims para este item
            const itemClaims = result.data
              .filter(claim => claim.item_id === item.id)
              .map(claim => ({
                userId: claim.user_id,
                userName: claim.user_name,
                quantity: claim.quantity,
                claimId: claim.id
              }));

            return {
              ...item,
              claims: itemClaims,
              _updateTimestamp: Date.now() // Fuerza re-render
            };
          });
        });
      }
    });
  }, [sessionId]);

  // 🔄 Función para recargar claims desde la base de datos
  const reloadClaimsFromDatabase = useCallback(async () => {
    if (!sessionId) return;

    console.log('🔄 Recargando claims desde la base de datos...');
    const result = await getAllSessionClaims(sessionId);

    if (result.success && result.data) {
      console.log(`✅ Recargados ${result.data.length} claims`);

      setBillItems(prevItems => {
        return prevItems.map(item => {
          const itemClaims = result.data
            .filter(claim => claim.item_id === item.id)
            .map(claim => ({
              userId: claim.user_id,
              userName: claim.user_name,
              quantity: claim.quantity,
              claimId: claim.id
            }));

          return {
            ...item,
            claims: itemClaims,
            _updateTimestamp: Date.now()
          };
        });
      });
    }
  }, [sessionId]);

  // 🔄 Refrescar datos cuando la app vuelve al foco (después de cambiar de pantalla)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👀 App volvió al foco - refrescando claims...');
        reloadClaimsFromDatabase();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reloadClaimsFromDatabase]);

  // Suscribirse a cambios en user_claims (real-time)
  useEffect(() => {
    if (!sessionId) return;

    console.log('📡 Subscribing to user_claims changes...');

    const unsubscribe = subscribeToSessionClaims(sessionId, (payload) => {
      console.log('📡 Claim change detected:', {
        eventType: payload.eventType,
        new: payload.new,
        old: payload.old
      });

      // Recargar todos los claims SIEMPRE (INSERT, UPDATE, DELETE)
      console.log('📥 Reloading all claims after', payload.eventType);
      getAllSessionClaims(sessionId).then(result => {
        console.log('📥 Claims reloaded:', result.success, result.data?.length);
        if (result.success && result.data) {
          setBillItems(prevItems => {
            const newItems = prevItems.map(item => {
              const itemClaims = result.data
                .filter(claim => claim.item_id === item.id)
                .map(claim => ({
                  userId: claim.user_id,
                  userName: claim.user_name,
                  quantity: claim.quantity,
                  claimId: claim.id
                }));

              return {
                ...item,
                claims: itemClaims,
                _updateTimestamp: Date.now() // Fuerza re-render
              };
            });
            console.log('🔄 BillItems updated via real-time');
            return newItems;
          });
        }
      });
    });

    return () => {
      console.log('🧹 Cleaning up claims subscription');
      unsubscribe();
    };
  }, [sessionId]);

  // If no bill data, log error
  useEffect(() => {
    if (!billData || !sessionId) {
      console.error('❌ No bill data or session ID in item-selection');
    }
    if (billItems.length === 0) {
      console.warn('⚠️ No items in billItems array');
    }
  }, [billData, sessionId, billItems]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!sessionId) {
      console.log('❌ No sessionId, skipping real-time subscription');
      return;
    }

    console.log('🔄 Setting up real-time subscription for session:', sessionId);

    const unsubscribe = subscribeToSession(sessionId, (updatedBillData) => {
      console.log('📡 Real-time update received in item-selection:', updatedBillData);
      if (updatedBillData) {
        setRealtimeBillData(updatedBillData);

        // Update billItems with new validated items
        const newTransformedItems = updatedBillData.items
          ?.filter(item => {
            if (isGuest) {
              return item.validated === true;
            }
            return true;
          })
          ?.map(item => {
            // Calculate unit price correctly
            const quantity = item.quantity || 1;
            const unitPrice = item.unitPrice || (item.totalPrice / quantity) || item.price || 0;

            return {
              id: item.id,
              name: item.name,
              description: item.description || '',
              price: unitPrice,
              unitPrice: unitPrice,
              totalPrice: item.totalPrice || (unitPrice * quantity),
              quantity: quantity,
              category: item.category || 'otros',
              allergens: item.allergens || [],
              validated: item.validated || false,
              claims: item.claims || [],
              shared: item.shared || false,
              sharedWith: item.sharedWith || []
            };
          }) || [];

        setBillItems(newTransformedItems);
        console.log('✅ Updated billItems from realtime data, count:', newTransformedItems.length);
      }
    });

    return () => {
      console.log('🧹 Cleaning up real-time subscription');
      unsubscribe();
    };
  }, [sessionId, isGuest]);

  const categories = [
    { value: 'all', label: 'Todos' },
    { value: 'entrantes', label: 'Entrantes' },
    { value: 'principales', label: 'Principales' },
    { value: 'postres', label: 'Postres' },
    { value: 'bebidas', label: 'Bebidas' }];


  const sortOptions = [
    { value: 'name', label: 'Nombre' },
    { value: 'price', label: 'Precio' },
    { value: 'category', label: 'Categoría' },
    { value: 'availability', label: 'Disponibilidad' }];


  // Filter and sort items
  const filteredAndSortedItems = billItems?.filter((item) => {
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item?.category === filterCategory;
    return matchesSearch && matchesCategory;
  })?.sort((a, b) => {
    switch (sortBy) {
      case 'price':
        return a?.price - b?.price;
      case 'category':
        return a?.category?.localeCompare(b?.category);
      case 'availability':
        const aRemaining = a?.quantity - (a?.claims?.reduce((sum, claim) => sum + claim?.quantity, 0) || 0);
        const bRemaining = b?.quantity - (b?.claims?.reduce((sum, claim) => sum + claim?.quantity, 0) || 0);
        return bRemaining - aRemaining;
      default:
        return a?.name?.localeCompare(b?.name);
    }
  });

  // Event handlers
  const handleClaimItem = async (itemId, quantity) => {
    const item = billItems.find(i => i.id === itemId);
    if (!item) return;

    const existingClaim = item.claims?.find(c => c.userId === currentUserId);
    // If the item is shared among several participants, claim the proportional share
    let claimAmount = quantity;
    if (item.shared && item.sharedWith && Array.isArray(item.sharedWith) && item.sharedWith.length > 0) {
      // Divide the item quantity evenly among the sharedWith participants
      const perPerson = (item.quantity || 1) / item.sharedWith.length;
      claimAmount = perPerson;
    }
    const newQuantity = existingClaim ? existingClaim.quantity + claimAmount : claimAmount;

    console.log('📝 Claiming item:', { itemId, currentQuantity: existingClaim?.quantity || 0, adding: quantity, newTotal: newQuantity });

    // Guardar en user_claims
    const result = await saveUserClaim(
      sessionId,
      currentUserId,
      userName,
      item.id,
      item.name,
      newQuantity,
      item.unitPrice
    );

    if (result.success) {
      console.log('✅ Claim saved to user_claims:', result.action);

      // Actualizar estado local INMEDIATAMENTE para UX rápida
      setBillItems(prevItems =>
        prevItems.map(i => {
          if (i.id === itemId) {
            const otherClaims = i.claims.filter(c => c.userId !== currentUserId);
            return {
              ...i,
              claims: [...otherClaims, {
                userId: currentUserId,
                userName: userName,
                quantity: newQuantity,
                claimId: result.data?.id || existingClaim?.claimId
              }],
              _updateTimestamp: Date.now() // Forzar re-render
            };
          }
          return i;
        })
      );
      // Real-time sincronizará con otros usuarios automáticamente
    } else {
      console.error('❌ Error saving claim:', result.error);
    }
  };

  const handleReleaseItem = async (itemId, quantity) => {
    const item = billItems.find(i => i.id === itemId);
    if (!item) return;

    const existingClaim = item.claims?.find(c => c.userId === currentUserId);
    if (!existingClaim) return;

    console.log('📤 Releasing item completely:', { itemId, userId: currentUserId, userName });

    // Guardar en user_claims con quantity=0 para eliminar
    const result = await saveUserClaim(
      sessionId,
      currentUserId,
      userName,
      item.id,
      item.name,
      0, // Cantidad 0 = eliminar claim
      item.unitPrice
    );

    if (result.success) {
      console.log('✅ Release saved to user_claims:', result.action);

      // Actualizar estado local INMEDIATAMENTE para UX rápida
      setBillItems(prevItems =>
        prevItems.map(i =>
          i.id === itemId
            ? {
              ...i,
              claims: i.claims.filter(c => c.userId !== currentUserId),
              _updateTimestamp: Date.now() // Forzar re-render
            }
            : i
        )
      );
      // Real-time sincronizará con otros usuarios automáticamente
    } else {
      console.error('❌ Error saving release:', result.error);
    }
  };

  const handleAdjustQuantity = async (itemId, newQuantity) => {
    const item = billItems.find(i => i.id === itemId);
    if (!item) return;

    console.log('🔧 Adjusting quantity:', { itemId, newQuantity });

    // Guardar en user_claims (si quantity=0, se eliminará)
    const result = await saveUserClaim(
      sessionId,
      currentUserId,
      userName,
      item.id,
      item.name,
      newQuantity,
      item.unitPrice
    );

    if (result.success) {
      console.log('✅ Quantity adjustment saved to user_claims:', result.action);
    } else {
      console.error('❌ Error saving adjustment:', result.error);
    }
  };

  const handleResolveConflict = (itemId, resolutionType, resolutionData) => {
    // Handle conflict resolution logic here
    console.log('Resolving conflict:', { itemId, resolutionType, resolutionData });
    setShowConflictModal(false);
  };

  const handleProceedToPayment = () => {
    console.log('💰 Proceeding to payment with data:', {
      sessionId,
      billData,
      billItems,
      userName,
      isHost,
      isGuest,
      currentUserId
    });

    // Log detailed claims info
    const itemsWithClaims = billItems.filter(item => item.claims && item.claims.length > 0);
    console.log('🔍 Items with claims:', itemsWithClaims);
    console.log('🔍 Total items:', billItems.length, 'Items with claims:', itemsWithClaims.length);

    // Los claims ya están guardados en user_claims, solo navegar
    navigate('/payment-summary', {
      state: {
        sessionId,
        billData,
        billItems,
        userName,
        isHost,
        isGuest
      }
    });
  };

  const handleInviteParticipant = () => {
    console.log('👥 Opening share panel');
    setShowSharePanel(true);
  };

  const handleRemoveParticipant = async (participantId) => {
    console.log('Removing participant from item-selection view:', participantId);
    if (!sessionId || !billData) return;

    const isSelf = String(participantId) === String(currentUserId);
    const confirmed = isSelf ? confirm('¿Quieres salir de la sesión?') : confirm('¿Eliminar a este comensal de la sesión?');
    if (!confirmed) return;

    const updatedParticipants = (billData.participants || []).filter(p => p.id !== participantId);
    const updatedBillData = { ...billData, participants: updatedParticipants };

    setRealtimeBillData(updatedBillData);

    // Persist
    const res = await saveSessionToCloud(sessionId, updatedBillData);
    if (!res.success) {
      console.error('Failed removing participant:', res.error);
    }

    if (isSelf) {
      navigate('/');
    }
  };

  const handleViewParticipants = () => {
    console.log('👥 Viewing participants');
    setSidebarView('participants');
    setIsMobileSidebarOpen(true);
  };

  const handleViewUserItems = (userId) => {
    console.log('Viewing items for user:', userId);
  };

  const handleBackToBillOverview = () => {
    console.log('⬅️ Navigating back to bill-overview');

    // Cargar datos frescos de Supabase antes de navegar
    if (sessionId) {
      getSessionFromCloud(sessionId).then(result => {
        if (result.success && result.data) {
          console.log('✅ Loaded fresh session data for bill-overview:', result.data);
          navigate('/bill-overview', {
            state: {
              sessionId,
              billData: result.data,
              userName,
              userId: currentUserId,
              isHost,
              isGuest
            }
          });
        } else {
          console.error('❌ Failed to load session data:', result.error);
          // Navegar con datos actuales como fallback
          navigate('/bill-overview', {
            state: {
              sessionId,
              billData,
              userName,
              userId: currentUserId,
              isHost,
              isGuest
            }
          });
        }
      });
    } else {
      // Si no hay sessionId, usar datos actuales
      navigate('/bill-overview', {
        state: {
          sessionId,
          billData,
          userName,
          userId: currentUserId,
          isHost,
          isGuest
        }
      });
    }
  };

  const getTotalProgress = () => {
    const totalItems = billItems?.reduce((sum, item) => sum + item?.quantity, 0);
    const claimedItems = billItems?.reduce((sum, item) => {
      const claimed = item?.claims?.reduce((claimSum, claim) => claimSum + claim?.quantity, 0) || 0;
      return sum + claimed;
    }, 0);

    return totalItems > 0 ? Math.round(claimedItems / totalItems * 100) : 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SessionProgressIndicator currentStep={2} />

      {/* No active session */}
      {!hasActiveSession && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-text-secondary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              No existen datos
            </h2>
            <p className="text-text-secondary mb-6">
              No hay una sesión activa. Por favor, escanea un ticket primero para crear una nueva sesión.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/receipt-scanner')}
              iconName="Camera"
              iconPosition="left"
            >
              Escanear Ticket
            </Button>
          </div>
        </div>
      )}

      {/* Active session content */}
      {hasActiveSession && (
        <>
          <CollaborativeStatusBar
            participants={participants}
            sessionStatus="active"
            currentUserId={currentUserId}
            onInviteParticipant={handleInviteParticipant}
            onViewParticipants={handleViewParticipants} />

          <div className="flex h-[calc(100vh-12rem)]">
            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Controls Bar */}
              <div className="bg-surface border-b border-border p-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
                  {/* Back Button + Search and Filters */}
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                    {/* Botón Volver y Búsqueda en la misma línea en móvil */}
                    <div className="flex space-x-2">
                      {/* Back to Bill Overview Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBackToBillOverview}
                        iconName="ArrowLeft"
                        iconSize={16}
                        className="flex-shrink-0"
                      >
                        <span className="hidden sm:inline">Editar Ticket</span>
                        <span className="sm:hidden">Volver</span>
                      </Button>

                      {/* Search */}
                      <div className="relative flex-1">
                        <Icon
                          name="Search"
                          size={16}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />

                        <Input
                          type="search"
                          placeholder="Buscar artículos..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e?.target?.value)}
                          className="pl-10" />

                      </div>
                    </div>
                  </div>

                  {/* Progress and Actions */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-text-secondary">Progreso:</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-border rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${getTotalProgress()}%` }} />

                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {getTotalProgress()}%
                        </span>
                      </div>
                    </div>

                    {/* Mobile Sidebar Toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                      className="lg:hidden"
                      iconName={sidebarView === 'summary' ? 'Receipt' : 'Users'}
                      iconSize={16}>

                      {sidebarView === 'summary' ? 'Resumen' : 'Usuarios'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Items Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {filteredAndSortedItems?.length > 0 ?
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredAndSortedItems?.map((item) =>
                      <ItemCard
                        key={item?.id}
                        item={item}
                        participants={participants}
                        onClaimItem={handleClaimItem}
                        onReleaseItem={handleReleaseItem}
                        onAdjustQuantity={handleAdjustQuantity}
                        currentUserId={currentUserId} />

                    )}
                  </div> :

                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      No se encontraron artículos
                    </h3>
                    <p className="text-text-secondary mb-4">
                      Intenta ajustar los filtros de búsqueda
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchTerm('');
                        setFilterCategory('all');
                      }}>

                      Limpiar filtros
                    </Button>
                  </div>
                }
              </div>
            </div>

            {/* Desktop Sidebar */}
            <div className="hidden lg:block">
              {sidebarView === 'summary' ?
                <UserSummaryPanel
                  participants={participants}
                  billItems={billItems}
                  currentUserId={currentUserId}
                  onProceedToPayment={handleProceedToPayment}
                  position="sidebar" /> :


                <ParticipantsList
                  participants={participants}
                  billItems={billItems}
                  currentUserId={currentUserId}
                  onViewUserItems={handleViewUserItems}
                  onRemoveParticipant={handleRemoveParticipant} />

              }
            </div>

            {/* Mobile Sidebar Overlay */}
            {isMobileSidebarOpen &&
              <>
                <div
                  className="fixed inset-0 bg-black/20 z-30 lg:hidden"
                  onClick={() => setIsMobileSidebarOpen(false)} />

                <div className="fixed right-0 top-0 h-full z-40 lg:hidden">
                  {sidebarView === 'summary' ?
                    <UserSummaryPanel
                      participants={participants}
                      billItems={billItems}
                      currentUserId={currentUserId}
                      onProceedToPayment={handleProceedToPayment}
                      position="sidebar" /> :


                    <ParticipantsList
                      participants={participants}
                      billItems={billItems}
                      currentUserId={currentUserId}
                      onViewUserItems={handleViewUserItems}
                      onRemoveParticipant={handleRemoveParticipant} />

                  }
                </div>
              </>
            }
          </div>
          {/* Floating Summary for Mobile */}
          <div className="lg:hidden">
            <UserSummaryPanel
              participants={participants}
              billItems={billItems}
              currentUserId={currentUserId}
              onProceedToPayment={handleProceedToPayment}
              position="floating" />

          </div>

          {/* Share Session Control - Removed floating button, only modal */}

          {/* Conflict Resolution Modal */}
          <ConflictResolutionModal
            isOpen={showConflictModal}
            onClose={() => setShowConflictModal(false)}
            conflictItem={conflictItem}
            conflictingUsers={conflictingUsers}
            onResolveConflict={handleResolveConflict}
            currentUserId={currentUserId} />

          {/* Sidebar View Toggle (Desktop) */}
          <div className="hidden lg:block fixed top-1/2 right-2 z-30">
            <div className="flex flex-col space-y-2">
              <Button
                variant={sidebarView === 'summary' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setSidebarView('summary')}
                title="Ver Resumen">

                <Icon name="Receipt" size={16} />
              </Button>
              <Button
                variant={sidebarView === 'participants' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setSidebarView('participants')}
                title="Ver Participantes">

                <Icon name="Users" size={16} />
              </Button>
            </div>
          </div>

          {/* Share Session Modal */}
          {showSharePanel && sessionId && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/50 z-50"
                onClick={() => setShowSharePanel(false)}
              />

              {/* Modal Content */}
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-surface border border-border rounded-lg shadow-elevation-3 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Compartir Sesión</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowSharePanel(false)}
                    >
                      <Icon name="X" size={20} />
                    </Button>
                  </div>

                  <ShareSessionControl
                    sessionId={sessionId}
                    position="inline"
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

};

export default ItemSelectionPage;